import React, { useState, useEffect, useRef } from 'react';
import { TextInput, ActionIcon } from '@mantine/core';
import { Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useCreateTodo, useTodoLists } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';
import { ListDropdown } from './ListDropdown';
import './LeanAddAndEditTaskDialog.css';

interface LeanAddAndEditTaskDialogProps {
  opened: boolean;
  onClose: () => void;
  selectedListId?: number | string; // Can be list ID or 'all'
}

export const LeanAddAndEditTaskDialog: React.FC<LeanAddAndEditTaskDialogProps> = ({ 
  opened, 
  onClose,
  selectedListId
}) => {
  const [title, setTitle] = useState('');
  const { currentUser } = useUser();
  const createMutation = useCreateTodo();
  const { data: todoLists = [] } = useTodoLists();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Manage list selection - use prop if provided, otherwise undefined (default list)
  const [listId, setListId] = useState<number | undefined>(() => {
    if (typeof selectedListId === 'number') return selectedListId;
    return undefined;
  });
  
  // Update listId when selectedListId prop changes
  useEffect(() => {
    if (typeof selectedListId === 'number') {
      setListId(selectedListId);
    } else if (selectedListId === 'all' || selectedListId === undefined) {
      // Keep current selection or use undefined
    }
  }, [selectedListId]);
  
  // Always show list selector so users can see and change the current list
  const showListSelector = true;
  
  // Get the selected list object for the dropdown
  const selectedList = todoLists.find(list => list.id === listId) || null;

  // No keyboard positioning - let the page naturally adjust its height
  // The dialog will stay at the bottom and the content area will shrink

  // No scroll class toggles anymore; allow natural layout behavior

  // Handle opening/closing with animations
  useEffect(() => {
    if (opened) {
      setShouldRender(true);
      setIsClosing(false);
      
      // Simple focus handling for all devices
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [opened, shouldRender]);



  // Handle clicks on background content - prevent todo interactions only
  useEffect(() => {
    if (!opened) return;

    const handleContentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // If click is on the dialog itself or its children, don't interfere
      if (sheetRef.current?.contains(target)) {
        return;
      }

      // Check if click is on a todo item or other interactive elements that should be blocked
      const isTodoItem = target.closest('[data-todo-item]') || 
                         target.closest('.todo-item') || 
                         target.closest('.todo-item-card') || // Main TodoItem component class
                         target.closest('.todo-list-item') ||
                         target.closest('.mantine-Card-root') || // Mantine card components
                         target.closest('.kanban-card') || // Kanban mode cards
                         target.closest('[role="button"]:not(.lean-task-sheet *)') ||
                         target.closest('button:not(.lean-task-sheet *)') || 
                         target.closest('a:not(.lean-task-sheet *)') ||
                         target.closest('.clickable') ||
                         target.closest('[data-testid*="todo"]') ||
                         target.closest('.todo-card') ||
                         target.closest('[class*="todo"]') ||
                         target.closest('[id*="todo"]');

      if (isTodoItem) {
        // Prevent todo item clicks but don't close dialog - just block the action
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // For clicks on non-interactive background areas, close the dialog
      // But be more selective - only close for clicks on specific "safe" areas
      const isSafeBackgroundArea = target.closest('.app-content') || 
                                   target.closest('.todos-container') ||
                                   target.closest('.main-content') ||
                                   target.matches('body') ||
                                   target.matches('html');

      if (isSafeBackgroundArea) {
        if (inputRef.current) {
          inputRef.current.blur();
        }
        onClose();
      }
    };

    // Use capture mode to intercept clicks before they reach the todo items
    document.addEventListener('click', handleContentClick, true);

    return () => {
      document.removeEventListener('click', handleContentClick, true);
    };
  }, [opened, onClose]);

  const handleSubmit = async () => {
    if (!title.trim() || !currentUser?.email) return;

    setIsSubmitting(true);
    try {
      const result = await createMutation.mutateAsync({
        title: title.trim(),
        createdBy: currentUser.email,
        priority: 'Medium',
        listId: listId, // Use the selected list
      });

      // Always scroll to and highlight the newly created todo
      if (result && result.id) {
        // Set URL params to trigger highlight, but delay the scroll behavior
        // to avoid interfering with the dialog position
        const newParams = new URLSearchParams(searchParams);
        newParams.set('taskId', result.id.toString());
        newParams.set('highlight', 'true');
        newParams.set('highlightMessage', 'New item added');
        newParams.set('listId', (result.listId || 'all').toString());
        newParams.set('assignee', 'any');
        
        // Add a flag to indicate the dialog is open during highlighting
        newParams.set('dialogOpen', 'true');
        setSearchParams(newParams, { replace: true });
        
        // Remove the dialogOpen flag after a short delay to allow normal scrolling later
        setTimeout(() => {
          const updatedParams = new URLSearchParams(searchParams);
          updatedParams.delete('dialogOpen');
          setSearchParams(updatedParams, { replace: true });
        }, 1000);
      }

      // Clear input but keep dialog open for rapid entry
      setTitle('');
      // Re-focus input for next task (after small delay to ensure navigation completes)
      setTimeout(() => {
        inputRef.current?.focus();
        setIsSubmitting(false);
      }, 100);
    } catch (error) {
      console.error('Failed to create task:', error);
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      // Clear input and close dialog
      setTitle('');
      inputRef.current?.blur();
      onClose();
    }
  };

  const handleBlur = () => {
    // When input loses focus, close dialog if empty
    setTimeout(() => {
      // Close dialog if input is empty and dialog is open
      if (opened && !title.trim()) {
        onClose();
      }
    }, 150);
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Visual backdrop only - no interaction blocking */}
      <div className={`lean-task-backdrop ${isClosing ? 'closing' : ''}`} />
      
      {/* Simple bottom-positioned dialog container */}
      <div className={`lean-task-container ${isClosing ? 'closing' : ''}`}>
        <div 
          ref={sheetRef}
          className={`lean-task-sheet ${isSubmitting ? 'submitting' : ''}`}
        >
          <div className="lean-task-content">
            <div className="lean-task-input-row">
              <TextInput
                ref={inputRef}
                placeholder={selectedList ? `Add task to ${selectedList.name}...` : "What needs to be done?"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                size="md"
                styles={{
                  root: {
                    flex: 1,
                  },
                  input: { 
                    fontSize: '16px', // Prevent zoom on mobile
                    backgroundColor: 'var(--app-bg)',
                    border: '1px solid var(--app-border)',
                    color: 'var(--app-text-primary)',
                    padding: '8px 12px',
                  }
                }}
                autoComplete="off"
              />
              
              {/* Show list selector inline so users can see and change the current list */}
              {showListSelector && (
                <div onMouseDown={(e) => e.preventDefault()} className="list-dropdown-container">
                  <ListDropdown
                    value={listId}
                    onChange={(newListId) => {
                      setListId(newListId);
                      // Prevent closing by refocusing after a tiny delay
                      setTimeout(() => {
                        if (inputRef.current) {
                          inputRef.current.focus();
                        }
                      }, 10);
                    }}
                    placeholder="List"
                    hideAllOption={true}
                    selectedList={selectedList}
                    hideMetadata={true}
                    compactMode={true}
                  />
                </div>
              )}
              
              <ActionIcon
                onClick={handleSubmit}
                disabled={!title.trim()}
                loading={createMutation.isPending}
                size="lg"
                color="blue"
                variant="filled"
                onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
                aria-label="Add task"
              >
                <Check size={20} />
              </ActionIcon>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
